/**
 * Avatar and image URL helpers.
 *
 * Prefer image variants from profile responses (avatarVariants.thumb, .thumbWebp)
 * over the full-size avatarUrl. The API returns pre-resized variants so we don't
 * need client-side CDN transforms.
 */

import type { ImageVariants } from "@cc-consulting-nv/ccsdk";

/**
 * Get the best avatar thumbnail URL from variants or fallback to the raw URL.
 * Prefers WebP thumb > JPEG thumb > small > raw URL.
 */
export function getAvatarThumbnailUrl(
  url: string | null | undefined,
  _size?: number,
  variants?: ImageVariants | null,
): string {
  // Prefer pre-resized variants from the API
  if (variants) {
    const thumb = variants.thumbWebp || variants.thumb || variants.smallWebp || variants.small;
    if (thumb) return thumb;
  }
  if (!url || typeof url !== "string") return "";
  return url.trim();
}

/**
 * Resolve a displayable URL from a post image/attachment item. The API may send
 * a string, `{ url }`, or `{ variants }` when `url` is missing until processing completes.
 */
export function resolvePostAttachmentImageUrl(item: unknown): string | null {
  if (item == null) return null;
  if (typeof item === "string") {
    const t = item.trim();
    return t || null;
  }
  if (typeof item !== "object") return null;
  const o = item as Record<string, unknown>;
  const url = o.url;
  if (typeof url === "string" && url.trim()) {
    return url.trim();
  }
  const variants = o.variants as Partial<ImageVariants> | null | undefined;
  if (variants && typeof variants === "object") {
    const fromVariants =
      variants.largeWebp ||
      variants.large ||
      variants.mediumWebp ||
      variants.medium ||
      variants.smallWebp ||
      variants.small ||
      variants.thumbWebp ||
      variants.thumb;
    if (typeof fromVariants === "string" && fromVariants.trim()) {
      return fromVariants.trim();
    }
  }
  for (const k of ["src", "href", "fullUrl", "full_url"] as const) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

/**
 * Like resolvePostAttachmentImageUrl but prefers the SMALLEST usable variant
 * (thumb 150px → small 500px) over the full-size url. For dense thumbnail grids
 * (e.g. the CSS3D orbital timeline) where a 256px slot must not decode a 1920px
 * source — 90 full-res decodes overrun the iOS WKWebView renderer.
 */
export function resolvePostThumbnailUrl(item: unknown): string | null {
  if (item && typeof item === "object") {
    const variants = (item as Record<string, unknown>).variants as
      | Partial<ImageVariants>
      | null
      | undefined;
    if (variants && typeof variants === "object") {
      const small =
        variants.smallWebp ||
        variants.small ||
        variants.thumbWebp ||
        variants.thumb;
      if (typeof small === "string" && small.trim()) return small.trim();
    }
  }
  return resolvePostAttachmentImageUrl(item);
}
